/**
 * The bindings: what actually stops each people fielding one more company.
 *
 * `RaceDef.binding` is the spine of the roster — a race changes the *shape* of a
 * constraint rather than its size, so "I want a bigger army" has a different
 * answer for every people. This file is the arithmetic half of that: the
 * capacities, the thresholds, the sentences, and nothing that mutates a world.
 *
 * ── Why the numbers live here and the enforcement does not ───────────────────
 *
 * Deliberately pure and deliberately import-light. `recomputePopCap` in
 * `world.ts` needs the grove term, `musterRoom` in `companies.ts` needs the
 * refusals, `damageUnit` in `combat.ts` needs the thrall threshold, `bots.ts`
 * needs all three and the UI needs the report — and every one of those files is
 * already somewhere in the `world → systems → world` import tangle. A file that
 * only reads `World`, `RaceDef` and `BuildingType` can be imported by all of
 * them without adding an edge that matters. The moment this file imports
 * `combat.ts` that stops being true, so it does not.
 *
 * The *doors* — the single places each binding is enforced — are named at each
 * function below and are all elsewhere. That split is the lesson `irreplaceable`
 * taught: a guard belongs on the one door the rule comes through, not on the
 * button the player happens to press, or the AI walks straight past it.
 *
 * ── What is built ────────────────────────────────────────────────────────────
 *
 *   `mana`           `musterRoom` / `recomputeManaCap`      — built before this
 *   `irreplaceable`  `reinforceCompany`                     — built before this
 *   `names`          `musterRoom`, `recruitCompany`, `stepConsecration`
 *   `groves`         `recomputePopCap`, `raceBuildRefusal`, `stepGroveDieback`
 *   `thralls`        `damageUnit`, `musterRoom`, `recruitCompany`
 *   `moot`           `stepPopulation`, `stepPress`, `musterRoom`
 *   `housing`        the baseline, and what an unbuilt binding falls through to
 *
 * `crews` is **not built**. It wants a per-unit toll column that `data/units.ts`
 * does not carry, and half of it — a machine charging heads it never gives back
 * — is indistinguishable from a bug unless the panel can show the running bill.
 * The Cog-Warrens play as a housed people, which is the honest failure mode: a
 * race that plays as everybody else rather than one that plays wrong.
 */

import type { World } from '../world.ts';
import { Team } from '../types.ts';
import { race } from '../../data/races.ts';
import { buildingType, type BuildingType } from '../../data/buildings.ts';
import { season } from './calendar.ts';

// ── The Gilded Vigil: consecrated names ────────────────────────────────────

/**
 * Seconds of the Vigil's one scribe's time per name.
 *
 * Priced against the muster it gates rather than against the clock: a build
 * phase is around ninety seconds, so a Vigil that loses a company in a siege
 * spends most of the following phase getting the name back before it can spend
 * a single coin on the men. That is the whole binding — the currency is *weeks*,
 * and coin cannot buy any of them.
 */
export const CONSECRATION_SECONDS = 92;

/** What a scriptorium takes off the naming. The building, not the node. */
export const SCRIPTORIUM_HASTE = 0.62;

/**
 * How many finished names the Vigil may hold at once.
 *
 * One, and two with a Scriptorium standing — which is exactly what the
 * `vig_scriptorium` node's rules text has always promised ("Two names may be
 * held in reserve"). The node unlocks the building and the building carries the
 * mechanic, so the promise is kept by the thing the player actually raises and
 * no new modifier key had to be invented to carry it.
 *
 * The cap is what makes this a production *line* rather than a bank. Without it
 * a Vigil that survived four quiet waves would arrive at the fifth holding six
 * names and no constraint at all.
 */
export function nameVaultCap(w: World): number {
  return 1 + Math.min(1, countStanding(w, 'scriptorium'));
}

export function consecrationSeconds(w: World): number {
  return CONSECRATION_SECONDS * (countStanding(w, 'scriptorium') > 0 ? SCRIPTORIUM_HASTE : 1);
}

// ── The Verdant Choir: groves ──────────────────────────────────────────────

/** Each completed grove past the first raises the yield of all of them. */
export const GROVE_COMPOUND = 0.18;

/**
 * What the season does to the grove term.
 *
 * Indexed by `season(w)`'s own order — Thaw, Long Sun, Tally, Hardfrost — so it
 * cannot drift from the table it is read against. The Choir's population is a
 * curve with a season in it: Tally is a fifth more heads than the groves
 * nominally carry and Hardfrost is not far off half. Nobody else's beds move.
 */
export const GROVE_SEASON = [0.9, 1.05, 1.2, 0.55];

export function groveSeasonMul(w: World): number {
  return GROVE_SEASON[seasonSlot(w)] ?? 1;
}

function seasonSlot(w: World): number {
  switch (season(w).id) {
    case 'thaw':
      return 0;
    case 'longsun':
      return 1;
    case 'tally':
      return 2;
    default:
      return 3;
  }
}

/**
 * A bed, as opposed to a grove or a barrack-room.
 *
 * Housing is `civic` and carries a `popCap`, which is true of the Hovels and the
 * Longhouse and of nothing else a Choir could raise. Written as a predicate over
 * the definition rather than as a list of two ids so that the third housing
 * building somebody adds next year is a house the day it is written.
 */
export function isHousing(def: BuildingType): boolean {
  return def.popCap > 0 && def.category === 'civic';
}

/**
 * A grove.
 *
 * The Heartgrove is `economy` and carries a `popCap`, which no other building in
 * the catalogue does: heads and harvest off the same ring, which is the whole
 * conceit. Same argument as `isHousing` for it being a predicate.
 */
export function isGrove(def: BuildingType): boolean {
  return def.popCap > 0 && def.category === 'economy';
}

// ── The Drowned Court: thralls ─────────────────────────────────────────────

/**
 * How badly hurt an enemy has to be before he is carried off rather than killed.
 *
 * A fifth, and a third once the Thrall Pens stand — which is what the `dro_pens`
 * node's rules text says in as many words. Same trick as the Scriptorium above:
 * the node unlocks the building, the building carries the mechanic.
 */
export const THRALL_HP_FRACTION = 0.2;
export const THRALL_HP_FRACTION_PENS = 1 / 3;

/**
 * What the ships carried up. Read by `initRun`; see the note there for why the
 * one binding whose currency is *taken* is the one binding that needs a float.
 */
export const OPENING_THRALLS = 24;

// ── The Litany: souls ──────────────────────────────────────────────────────

/**
 * What is already on the shelves, and why the second binding whose currency is
 * *taken* needs a float exactly as the first one does.
 *
 * The note on `OPENING_THRALLS` above worked the argument out once and then it
 * was not applied to the people it fits best. **Every unit on the Litany's
 * roster is priced in souls** — a Wretch at eight, a Boneshield at twenty-two, a
 * Barrow-wyrm at a hundred and ninety — and `initRun` grants `souls: 0`. Souls
 * come from killing, killing wants soldiers, and soldiers want souls. That is
 * the Court's deadlock word for word, in a faction that has shipped with it.
 *
 * It is not quite a *hard* deadlock — a tower or the Hearth itself can kill, and
 * a kill pays souls — which is exactly what made it survive: from the outside it
 * reads as a slow faction rather than a broken one. It is not slow. Measured
 * with `npm run climb 24 litany`, the balanced bot's median payroll over
 * twenty-four Endless runs was **0.0 a minute** — it played every run from the
 * first tick to the last with no soldiers at all, and was overrun in 96% of them
 * against the Ashen's 83%. Souls do trickle in from tower kills and then leak
 * back out again: `stepSettlement` charges an upkeep of `pop * 0.004` a second
 * against them, so a town of twelve never accumulates the two hundred a banner
 * costs.
 *
 * **Two halves, both derived, and the first version had only one of them.**
 *
 * *The company.* On the same parity as `MANA_BASE` and deliberately to the same
 * company, so the two gates open together rather than one at a time: a Wretch at
 * its quarter-strength floor is twenty models at eight souls. **160.**
 *
 * *The table.* Souls are not only this people's war chest, they are its
 * **food** — `stepUpkeep` charges `pop * 0.004` a second against them and says
 * so in as many words: "the Pale Litany eats nothing and pays in Souls
 * instead". So the 240 grain and 40 bread in `initRun`'s opening grant are
 * *wasted* on them, and a float sized only for a company is a war chest that
 * gets eaten. Measured: at 160 the drain took it to 108 by wave two, which is
 * the wave the barracks finishes on — so the trainer arrived exactly in time for
 * the money to be gone, and the bot raised nothing in five runs out of six.
 *
 * A housed people opens with a larder worth 340 grain-equivalents against an
 * appetite of 5.04 a minute at twelve heads: **67.5 minutes** of table. The same
 * buffer at 2.88 souls a minute is 194.
 *
 * 194 + 160 = 354, rounded to **350**. Re-derive it if the opening grant, the
 * opening head count, or that 0.004 moves.
 */
export const OPENING_SOULS = 350;

export function thrallThreshold(w: World): number {
  return countStanding(w, 'thrall_pens') > 0 ? THRALL_HP_FRACTION_PENS : THRALL_HP_FRACTION;
}

// ── The Splitmoot: the press ───────────────────────────────────────────────

/**
 * Heads the warren will not give up, whatever is asked of it.
 *
 * A released company takes goblins out of the warren rather than adding them to
 * the settlement, so without a floor the first muster of a fresh run would strip
 * every hand off the woodcutters and the town would never build anything again.
 */
export const MOOT_KEEP = 8;

/**
 * Building damage per second, per head of press over the line.
 *
 * Read against the Hovels, which are the cheapest thing a warren can be over the
 * line by: twenty heads over takes one down in about thirteen seconds, which is
 * long enough to notice and short enough to matter. Nothing clamps it, on
 * purpose — the passive says the mob "does not stop until it has room", and a
 * warren two hundred over its tolerance genuinely should be eating the town.
 */
export const MOOT_PRESS_DAMAGE = 0.85;

/**
 * The warren's clock, against the settlement's own `POP_GROWTH_SECONDS` of 14.
 *
 * Larger than everybody else's and yet much faster in practice, because the
 * accumulator it is compared against rises with the square root of the warren
 * rather than sitting near one: about twelve seconds a goblin at a dozen of
 * them, four at a hundred, and accelerating. The root rather than a straight
 * multiple is what keeps it a curve — see the note in `stepPopulation`.
 */
export const MOOT_BREED_SECONDS = 26;

/**
 * What the settlement will take before the mob turns on it.
 *
 * `w.popCap` — deliberately the same number every other people is capped by,
 * because the Splitmoot's difference is not that the line is somewhere else, it
 * is that **nothing refuses at it**. Every building that would have been a bed
 * for anybody else is a bit more room to shout in here, and every `popCap`
 * modifier the Shouting buys widens it without a line of its own.
 */
export function pressTolerance(w: World): number {
  return w.popCap;
}

/** Heads over the line. Only the warren presses; a company in the field does not. */
export function mootPress(w: World): number {
  return Math.max(0, w.counts.villagers - pressTolerance(w));
}

// ── The one refusal that is not about the muster ───────────────────────────

/**
 * What this people may not build *because of what it is*, or `''`.
 *
 * Called from `buildRefusal` in `apply.ts`, which is the single standing gate
 * every build passes — the menu greys off it and the click is refused by it, so
 * the sentence on the greyed button and the sentence at the moment of the click
 * are the same string and cannot drift.
 *
 * A refusal rather than a silent no-op, and that is the whole reason this
 * function exists. The Choir's houses *could* simply have contributed nothing to
 * the cap, which is one line shorter and reads to a player as a bug: thirty
 * timber for a building that does not do the one thing its own tooltip says it
 * does. Say no, and say why.
 */
export function raceBuildRefusal(w: World, typeIdx: number): string {
  if (race(w.raceId).binding !== 'groves') return '';
  if (!isHousing(buildingType(typeIdx))) return '';
  return 'The Choir sleeps in no house. Grow a grove; the heads come with it.';
}

// ── What the panel says ────────────────────────────────────────────────────

export interface BindingReport {
  /** The binding's proper name, from `BINDING_NAMES`. */
  name: string;
  /** What is held now, in whatever this people counts in. */
  have: number;
  /** What may be held, or -1 where the answer is genuinely "no limit". */
  cap: number;
  /** One line of exact mechanical text for the strip. */
  line: string;
  /** Non-empty when the next muster would be refused, and why. */
  blocked: string;
}

/**
 * The binding, as a panel would print it.
 *
 * The simulation decides *and* supplies the words, which is this project's one
 * strong convention — `musterRoom().why`, `reinforceError`, `researchError`,
 * `postReason`, `buildRefusal`, `marchRefusal`. A UI that had to compose these
 * sentences itself would compose eight different ones and five of them would be
 * subtly wrong about a rule it cannot see.
 */
export function bindingReport(w: World): BindingReport {
  const def = race(w.raceId);
  const name = BINDING_LABEL[def.binding] ?? 'Beds and Wages';
  switch (def.binding) {
    case 'mana':
      return {
        name,
        have: w.mana,
        cap: w.manaMax,
        line: `${Math.round(w.mana)} of ${w.manaMax} standing. Widen the pool to hold more.`,
        blocked: w.mana >= w.manaMax ? 'The pool will not hold them.' : '',
      };
    case 'names': {
      const cap = nameVaultCap(w);
      const secs = consecrationSeconds(w);
      const left = Math.max(0, secs - w.nameWork);
      return {
        name,
        have: w.consecratedNames,
        cap,
        line:
          w.consecratedNames >= cap
            ? `${w.consecratedNames} of ${cap} names ready. The scribe is idle.`
            : `${w.consecratedNames} of ${cap} names ready; the next in ${Math.ceil(left)}s.`,
        blocked: w.consecratedNames < 1 ? 'No name is ready.' : '',
      };
    }
    case 'groves': {
      const s = season(w);
      return {
        name,
        have: w.pop,
        cap: w.popCap,
        line: `${w.pop} of ${w.popCap} heads. The groves carry ${Math.round(groveSeasonMul(w) * 100)}% in ${s.name}.`,
        blocked: w.pop >= w.popCap ? 'The groves carry no more than this.' : '',
      };
    }
    case 'thralls':
      return {
        name,
        have: w.thralls,
        cap: -1,
        line: `${Math.floor(w.thralls)} taken. Enemies broken below ${Math.round(thrallThreshold(w) * 100)}% of health are carried off.`,
        blocked: w.thralls < 1 ? 'You have taken nobody.' : '',
      };
    case 'moot': {
      const press = mootPress(w);
      return {
        name,
        have: w.counts.villagers,
        cap: -1,
        line:
          press > 0
            ? `${w.counts.villagers} in the warren, ${Math.round(press)} over what the town will take. They are pulling it down.`
            : `${w.counts.villagers} in the warren; the town tolerates ${pressTolerance(w)}.`,
        blocked:
          w.counts.villagers - MOOT_KEEP < 1 ? 'The warren has nobody spare.' : '',
      };
    }
    case 'irreplaceable':
      return {
        name,
        have: w.pop,
        cap: w.popCap,
        line: `${w.pop} of ${w.popCap}. Nothing here is replaced; companies are raised whole or not at all.`,
        blocked: w.pop >= w.popCap ? 'There is nowhere for them to sleep.' : '',
      };
    default:
      return {
        name,
        have: w.pop,
        cap: w.popCap,
        line: `${w.pop} of ${w.popCap} beds.`,
        blocked: w.pop >= w.popCap ? 'There is nowhere for them to sleep.' : '',
      };
  }
}

/**
 * Local rather than imported from `races.ts`, because `BINDING_NAMES` there is
 * keyed by `BindingId` and this switch has a default arm for a binding that does
 * not exist yet. One string table, two readers, no import cycle worth the name.
 */
const BINDING_LABEL: Record<string, string> = {
  housing: 'Beds and Wages',
  mana: 'Mana',
  names: 'Consecrated Names',
  groves: 'The Grove',
  thralls: 'Thralls',
  moot: 'The Moot',
  crews: 'Crews',
  irreplaceable: 'The Irreplaceable',
};

// ── Shared ─────────────────────────────────────────────────────────────────

/**
 * Completed player buildings of this id.
 *
 * A walk of the building store, not of the population, and called from the four
 * places above at human rather than tick frequency — `thrallThreshold` is the
 * exception and rides a branch that is already false for ten races in eleven.
 */
export function countStanding(w: World, id: string): number {
  let n = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1 || b.team[i] !== Team.Player) continue;
    if (buildingType(b.typeId[i]).id === id) n++;
  }
  return n;
}
